import React, { useCallback, useState } from "react";
import { graphql } from "react-relay";

import { faEyeSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faEye } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import Menu from "~/ui/menu";
import { useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import { streamStore as store } from "../../utils";
import WatchModal, { SHOW_AGAIN_KEY } from "../../WatchModal";

import type { WatchStreamToggleMutation as ToggleWatchMutationType } from "~/__generated__/WatchStreamToggleMutation.graphql";

const ToggleWatchMutation = graphql`
	mutation WatchStreamToggleMutation($id: ID!, $watching: Boolean!) @raw_response_type {
		noteStreamToggleWatch(id: $id, watching: $watching) {
			noteStream {
				id
				watching
			}
		}
	}
`;

export async function watchStream(
	id: string,
	watching: boolean,
	{ optimistic, allowErrorToast } = { optimistic: false, allowErrorToast: true },
) {
	try {
		let optimisticResponse = undefined;
		if (optimistic) {
			optimisticResponse = {
				noteStreamToggleWatch: {
					noteStream: {
						id,
						watching,
					},
				},
			};
		}

		const { noteStreamToggleWatch } = await commitMutationPromise<ToggleWatchMutationType>(environment, {
			mutation: ToggleWatchMutation,
			variables: { id, watching },
			optimisticResponse,
		});

		if (!noteStreamToggleWatch) {
			throw new Error(`Failed to ${watching ? "" : "un"}watch stream`);
		}
	} catch (e) {
		allowErrorToast && toast.error("Failed to watch stream");
	}
}

interface Props {
	streamId: string;
	watching: boolean;
}

function showModal(): boolean {
	return store.get(SHOW_AGAIN_KEY, true);
}

export function WatchStreamMenuItem({ watching, streamId }: Props) {
	const [loading, setLoading] = useState(false);

	const modals = useModals();
	const openModal = useCallback(() => modals.open(({ close }) => <WatchModal onClose={close} />), [modals]);

	const toggleWatch = useCallback(async () => {
		const newWatchState = !watching;
		setLoading(true);
		await watchStream(streamId, newWatchState);
		setLoading(false);

		if (newWatchState && showModal()) {
			openModal();
		}
	}, [watching, streamId, openModal]);

	return (
		<Menu.Item
			onSelect={loading ? undefined : toggleWatch}
			data-test-id="watch-icon"
			data-watch-icon-state={watching ? "watching" : "not watching"}
			icon={watching ? faEyeSlash : faEye}
		>
			{watching ? "Unwatch this series" : "Watch this series"}
		</Menu.Item>
	);
}

export default WatchStreamMenuItem;
